import React, { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
  PageWrapper,
  ActionsBar,
  ActionButton,
  InvoicePaper,
  InvoiceHeader,
  CompanyName,
  InvoiceMetaBlock,
  Divider,
  TwoColumnSection,
  AddressBlock,
  AddressLabel,
  AddressLine,
  SectionTable,
  LineItemsTable,
  TotalsSection,
  TotalsTable,
  TotalsRow,
  NotesSection,
  NotesLabel,
  NotesText,
  Footer,
  StatusState,
} from './Invoice.styles';
import { getInvoice } from '../../../services/general';

// ─── Static issuer details ───────────────────────────────────────────────────
// Falls back to these if `generalData` from the API doesn't supply them.
const DEFAULT_ISSUER = {
  name: 'Urban Compass',
  addressLines: ['315/566 St Kilda Rd Melbourne', 'Melbourne', 'Victoria 3000'],
  email: 'info@urbancompasssoftware.com',
};

const TAX_RATE = 0.1;

// ─── helpers ────────────────────────────────────────────────────────────────

const formatDate = (isoDate) => {
  if (!isoDate) return '--';
  return new Date(isoDate).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const formatMoney = (value) => {
  // const num = Number(value) || 0;
  // if (num !== 0 && Math.abs(num) < 0.01) {
  //   // Keep small fractional costs (e.g. lambda invocations) from rounding to 0.00
  //   return num.toFixed(4);
  // }
  // return num.toFixed(2);

  return value;
};

const totalMoneyFormat = (value) => {
  const num = Number(value) || 0;

  return num.toFixed(2);
};

const roundingUp = (value) => {
  const num = Number(value) || 0;
  if (num !== 0 && Math.abs(num) < 0.01) {
    // Keep small fractional costs (e.g. lambda invocations) from rounding to 0.00
    return num.toFixed(4);
  }
  return num.toFixed(4);
};

const buildInvoiceNumber = (invoice) => {
  if (!invoice?._id) return 'N/A';
  const date = new Date(invoice.createdAt);
  const year = date.getFullYear();
  const suffix = invoice._id.slice(-4).toUpperCase();
  return `INV-${year}-${suffix}`;
};

const Invoice = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const printableRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoice({ invoiceId }),
    enabled: !!invoiceId,
  });

  const invoiceData = data?.invoiceData;
  const generalData = data?.generalData ?? {};

  const handleBack = () => navigate(-1);

  const handleDownloadPdf = async () => {
    if (!printableRef.current) return;

    setIsDownloading(true);
    try {
      const canvas = await html2canvas(printableRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');

      // A4 in points: 595.28 x 841.89
      const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add extra pages if the invoice is taller than one A4 page
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = invoiceData?.title
        ? `${invoiceData.title.replace(/\s+/g, '_')}.pdf`
        : `invoice_${invoiceId}.pdf`;

      pdf.save(fileName);
    } catch (error) {
      window.alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <PageWrapper>
        <StatusState>Loading invoice…</StatusState>
      </PageWrapper>
    );
  }

  if (isError || !invoiceData) {
    return (
      <PageWrapper>
        <StatusState>Failed to load invoice. Please try again later.</StatusState>
      </PageWrapper>
    );
  }

  const {
    title,
    createdAt,
    inputTokens = 0,
    outputTokens = 0,
    totalTokens = 0,
    ratePerKInputTokens = 0,
    ratePerKOutputTokens = 0,
    vectorDBRate = 0,
    mongoDBRate = 0,
    totalLambdaInvocations = 0,
    perLambdaInvocation = 0,
    chatbotId,
  } = invoiceData;

  const issuer = {
    name: generalData?.companyName || DEFAULT_ISSUER.name,
    addressLines: generalData?.addressLines || DEFAULT_ISSUER.addressLines,
    email: generalData?.email || DEFAULT_ISSUER.email,
  };

  // ── Cost calculations ─────────────────────────────────────────────────────
  const inputTokenCost = (inputTokens / 1000) * ratePerKInputTokens;
  const outputTokenCost = (outputTokens / 1000) * ratePerKOutputTokens;
  const lambdaCost = totalLambdaInvocations * perLambdaInvocation;
  const vectorDbCost = vectorDBRate;
  const mongoDbCost = mongoDBRate;

  const subtotal = inputTokenCost + outputTokenCost + lambdaCost + vectorDbCost + mongoDbCost;
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax;

  return (
    <PageWrapper>
      <ActionsBar>
        <ActionButton type="button" onClick={handleBack}>
          <ArrowLeft />
          Back
        </ActionButton>

        <ActionButton type="button" $primary onClick={handleDownloadPdf} disabled={isDownloading}>
          {isDownloading ? <Loader2 className="spin" /> : <Download />}
          {isDownloading ? 'Preparing PDF…' : 'Download PDF'}
        </ActionButton>
      </ActionsBar>

      <InvoicePaper ref={printableRef}>
        <InvoiceHeader>
          <CompanyName>{issuer.name}</CompanyName>
          <InvoiceMetaBlock>
            <div>
              <strong>Invoice #:</strong> {buildInvoiceNumber(invoiceData)}
            </div>
            <div>
              <strong>Date:</strong> {formatDate(createdAt)}
            </div>
          </InvoiceMetaBlock>
        </InvoiceHeader>

        <Divider />

        <TwoColumnSection>
          <AddressBlock>
            <AddressLabel>From:</AddressLabel>
            <AddressLine>{issuer.name}</AddressLine>
            {issuer.addressLines.map((line) => (
              <AddressLine key={line}>{line}</AddressLine>
            ))}
            <AddressLine>Email: {issuer.email}</AddressLine>
          </AddressBlock>

          <AddressBlock $align="right">
            <AddressLabel>Bill To:</AddressLabel>
            <AddressLine>{chatbotId?.councilName || '--'}</AddressLine>
            {chatbotId?.siteLink && (
              <AddressLine>
                <a href={chatbotId.siteLink} target="_blank" rel="noopener noreferrer">
                  {chatbotId.siteLink}
                </a>
              </AddressLine>
            )}
          </AddressBlock>
        </TwoColumnSection>

        <SectionTable>
          <thead>
            <tr>
              <th>Description</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Input Tokens Consumed This Month</td>
              <td>{inputTokens}</td>
            </tr>
            <tr>
              <td>Output Tokens Consumed This Month</td>
              <td>{outputTokens}</td>
            </tr>
            <tr>
              <td>Total Tokens Consumed This Month</td>
              <td>{totalTokens}</td>
            </tr>
            <tr>
              <td>Total Lambda Invocations</td>
              <td>{totalLambdaInvocations}</td>
            </tr>
            <tr>
              <td>Vector Database Cost for 250 MB</td>
              <td>{formatMoney(vectorDBRate)} USD</td>
            </tr>
            <tr>
              <td>Non-Relational Database Cost</td>
              <td>{formatMoney(mongoDBRate)} USD</td>
            </tr>
            <tr>
              <td>Rate Per 1000 Input Tokens</td>
              <td>{formatMoney(ratePerKInputTokens)} USD</td>
            </tr>
            <tr>
              <td>Rate Per 1000 Output Tokens</td>
              <td>{formatMoney(ratePerKOutputTokens)} USD</td>
            </tr>
            <tr>
              <td>Rate Per Lambda Invocation</td>
              <td>{formatMoney(perLambdaInvocation)} USD</td>
            </tr>
          </tbody>
        </SectionTable>

        <LineItemsTable>
          <thead>
            <tr>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit Price (USD)</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Input Token Cost</td>
              <td>{inputTokens}</td>
              <td>{formatMoney(ratePerKInputTokens)}</td>
              <td>{roundingUp(inputTokenCost)}</td>
            </tr>
            <tr>
              <td>Output Token Cost</td>
              <td>{outputTokens}</td>
              <td>{formatMoney(ratePerKOutputTokens)}</td>
              <td>{roundingUp(outputTokenCost)}</td>
            </tr>
            <tr>
              <td>Lambda Cost</td>
              <td>{totalLambdaInvocations}</td>
              <td>{formatMoney(perLambdaInvocation)}</td>
              <td>{roundingUp(lambdaCost)}</td>
            </tr>
            <tr>
              <td>Vector Database Cost for 250 MB</td>
              <td>-</td>
              <td>-</td>
              <td>{formatMoney(vectorDbCost)}</td>
            </tr>
            <tr>
              <td>Non-Relational Database Cost</td>
              <td>-</td>
              <td>-</td>
              <td>{formatMoney(mongoDbCost)}</td>
            </tr>
          </tbody>
        </LineItemsTable>

        <TotalsSection>
          <TotalsTable>
            <TotalsRow>
              <span>Subtotal</span>
              <span>{totalMoneyFormat(subtotal)} USD</span>
            </TotalsRow>
            <TotalsRow>
              <span>Tax ({TAX_RATE * 100}%)</span>
              <span>{totalMoneyFormat(tax)} USD</span>
            </TotalsRow>
            <TotalsRow $bold>
              <span>Total</span>
              <span>{totalMoneyFormat(total)} USD</span>
            </TotalsRow>
          </TotalsTable>
        </TotalsSection>

        <NotesSection>
          <NotesLabel>Notes:</NotesLabel>
          <NotesText>
            Please make the payment within 14 days of the invoice date. If you have any questions
            regarding this invoice, feel free to contact our finance team.
          </NotesText>
        </NotesSection>

        <Footer>
          © {new Date(createdAt).getFullYear()} {issuer.name}. All rights reserved.
        </Footer>
      </InvoicePaper>
    </PageWrapper>
  );
};

export default Invoice;
